"use client"

import { Suspense, useEffect, useLayoutEffect, useRef } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import posthog from "posthog-js"
import { PostHogProvider as PHProvider } from "posthog-js/react"

import {
  filterPosthogEvent,
  posthogPrivacyConfig,
  synchronizePosthogIdentity,
  type PosthogUserIdentity,
} from "@/lib/posthog-privacy"
import { useAppAuthState } from "@/hooks/auth/use-app-auth-state"

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  return (
    <PHProvider client={posthog}>
      <Suspense fallback={null}>
        <PostHogTracking />
      </Suspense>
      {children}
    </PHProvider>
  )
}

function PostHogTracking() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { currentUser, isSignedIn, clerkUserId, isLoading } = useAppAuthState({ includeCurrentUser: true })
  const identity = useRef<PosthogUserIdentity | undefined>(undefined)
  const readyUserId = !isLoading && isSignedIn && currentUser?.clerk_id === clerkUserId ? clerkUserId : null

  // Close the gate before passive effects elsewhere can capture on an auth
  // transition. Loading is not a confirmed sign-out.
  useLayoutEffect(() => {
    identity.current = undefined
  }, [readyUserId])

  useEffect(() => {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
      person_profiles: "identified_only",
      ...posthogPrivacyConfig,
      before_send: (event) => filterPosthogEvent(event, identity.current),
      opt_out_capturing_by_default: process.env.NODE_ENV === "development" || process.env.SLANT_ENV === "staging",
    })
    return () => {
      identity.current = undefined
    }
  }, [])

  useEffect(() => {
    if (isLoading) return
    const user =
      readyUserId && currentUser
        ? { distinctId: readyUserId, email: currentUser.email, username: currentUser.full_name }
        : undefined
    synchronizePosthogIdentity(posthog, user, (value) => {
      identity.current = value
    })
  }, [readyUserId, currentUser, isLoading])

  // Identity synchronization precedes the initial authenticated pageview.
  useEffect(() => {
    if (!readyUserId || process.env.NODE_ENV === "development" || process.env.SLANT_ENV === "staging") return
    const url = new URL(pathname, window.origin)
    url.search = searchParams.toString()
    posthog.capture("$pageview", { $current_url: url.toString() })
  }, [pathname, searchParams, readyUserId])

  return null
}
